/**
 * Pure helpers used by AiAutoReplyAnalyticsReportPage.
 *
 * Kept in a separate module so they're trivially unit-testable without
 * pulling in Redux, the router or the API client.
 */

export const ENDPOINT = 'office/ai-auto-reply-analytics-report'
export const EXPORT_ENDPOINT = 'office/ai-auto-reply-analytics-report/export'

/**
 * Build a query string for the analytics endpoints from the current filters.
 *
 * - Empty / nullish values are dropped (the controller will fall back to its
 *   "current month" / "all countries" / "all providers" defaults).
 * - Country is left as-is (the controller uppercases it server-side).
 */
export const buildQueryString = (
    startDate: string,
    endDate: string,
    country: string | null,
    providerId: string,
): string => {
    const params = new URLSearchParams()
    if (startDate !== '') {
        params.set('start_date', startDate)
    }
    if (endDate !== '') {
        params.set('end_date', endDate)
    }
    if (country !== null && country !== '') {
        params.set('country', country)
    }
    if (providerId !== '') {
        params.set('provider_id', providerId)
    }
    return params.toString()
}

const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export const defaultStartDate = (): string => {
    const now = new Date()
    return formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 1))
}

export const defaultEndDate = (): string => {
    const now = new Date()
    return formatLocalDate(new Date(now.getFullYear(), now.getMonth() + 1, 0))
}

export const formatPercent = (value: number): string => `${value.toFixed(1)}%`

/**
 * Format a minutes-to-reply value into a compact human label (e.g. "12m",
 * "3.5h", "2.1d").
 *
 * null means "no demand reply, so no data" and renders as a dash. A value of 0
 * is a real sub-minute reply (rendered "<1m"), kept distinct from null.
 */
export const formatMinutes = (value: number | null): string => {
    if (value === null) {
        return '–'
    }
    if (value < 1) {
        return '<1m'
    }
    if (value < 60) {
        return `${Math.round(value)}m`
    }
    if (value < 60 * 24) {
        return `${(value / 60).toFixed(1)}h`
    }
    return `${(value / (60 * 24)).toFixed(1)}d`
}
